// Imports
import React, { PropsWithChildren, useEffect, useRef } from 'react';
import styled, { useTheme } from 'styled-components/macro';
import ReactDOM from 'react-dom';
import Button from '../../foundation/front-end/components/buttons/standard';
import { faXmark } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { darken, transparentize } from 'polished';
import modalHelpers from './modal-helpers';


// Styled elements
const Container = styled.div`
	display: none;
	
	&.is-open {
		display: block;
	}
	
	> div:first-of-type {
		animation: fadeIn 0.3s ease-in-out;
	}
	
	div[role='dialog'] {
		animation: appear 0.3s ease-in-out;
	}
	
	&[aria-hidden='true'] {
		> div:first-of-type {
			animation: fadeOut 0.15s ease-in-out;
		}
		
		div[role='dialog'] {
			animation: disappear 0.15s ease-in-out;
		}
	}
	
	@keyframes fadeIn {
		from {
			opacity: 0;
		}
		
		to {
			opacity: 1;
		}
	}
	
	@keyframes fadeOut {
		from {
			opacity: 1;
		}
		
		to {
			opacity: 0;
		}
	}
	
	@keyframes appear {
		0% {
			transform: scale(0.7);
		}
		
		45% {
			transform: scale(1.05);
		}
		
		80% {
			transform: scale(0.95);
		}
		
		100% {
			transform: scale(1);
		}
	}
	
	@keyframes disappear {
		from {
			transform: scale(1);
		}
		
		to {
			transform: scale(0.5);
		}
	}
`;

const Fade = styled.div`
	width: 100vw;
	height: 100dvh;
	top: 0;
	left: 0;
	position: fixed;
	background: rgba(0, 0, 0, 0.4);
	z-index: 100;
	display: flex;
	align-items: center;
	justify-content: center;
`;

const Dialog = styled.div<{ wide?: true }>`
	max-width: min(90%, ${(props) => (props.wide ? '48rem' : '31.25rem')});
	${(props) => props.wide && 'min-width: min(90%, 31.25rem);'}
	background: #fff;
	z-index: 101;
	border-radius: 0.4rem;
	padding: 1.5rem;
	z-index: 101;
	will-change: transform;
	
	max-height: 95%;
	overflow: auto;
	overflow-wrap: anywhere;
	
	contain: layout;
	
	> p {
		&:not(:last-child) {
			margin-bottom: 0.5rem;
		}
	}
`;

const CloseButton = styled(Button)<{ $overrideColor?: string }>`
	position: absolute;
	right: 1.5rem;
	
	${({ $overrideColor }) =>
		$overrideColor &&
		`
			background-color: ${$overrideColor};
			
			&:hover,
			&:focus {
				background-color: ${darken(0.05, $overrideColor)} !important;
				
				box-shadow: 0 0 0 0.125em #fff, 0 0 0 0.25em ${transparentize(0.75, darken(0.05, $overrideColor))} !important;
			}
		`}
`;

const PaddedHeader = styled.header`
	padding-bottom: 1rem;
	margin-right: 3.25rem;
`;

const PlainHeader = styled.header`
	margin-right: 3.25rem;
`;

const Title = styled.strong`
	max-width: 500px;
	padding: 0;
	line-height: 1;
	font-size: 1.75rem;
	font-weight: 600;
	color: #222;
`;


// Define the accepted props
interface Props extends PropsWithChildren {
	identifier: string;
	title: string | React.ReactElement;
	wide?: true;
	onClose?: (closeMethod?: 'mouse') => void;
}


// Function component
const Modal: React.FC<Props> = ({ identifier, title, children, wide, onClose }) => {
	// Use styled components functionality
	const templateTheme = useTheme();
	
	
	// Get the base color safely if one exists
	function getTemplateBaseColor(templateTheme: unknown): string | undefined {
		if (
			templateTheme !== null &&
			typeof templateTheme === 'object' &&
			'baseColor' in templateTheme &&
			typeof templateTheme.baseColor === 'string'
		) {
			return templateTheme.baseColor;
		}
	}
	
	
	// Initialize
	const communityThemeColor = getTemplateBaseColor(templateTheme);
	
	
	// Handle close
	const handleClose = () => {
		if (onClose) {
			onClose('mouse');
			return;
		}
		
		modalHelpers.close(`${identifier}-modal`);
	};
	
	
	// Handle escape key
	useEffect(() => {
		// Return early
		if (!onClose) {
			return;
		}
		
		
		// Function that handles escape key press
		const handleKeyDown = (event: KeyboardEvent) => {
			// Return early
			if (event.key !== 'Escape') {
				return;
			}
			
			
			// Get modal element
			const modalElement = document.getElementById(`${identifier}-modal`);
			
			
			// Handle opened modal
			if (modalElement?.classList.contains('is-open')) {
				event.stopPropagation();
				onClose();
			}
		};
		
		
		// Add event listener
		window.addEventListener('keydown', handleKeyDown, true);
		
		
		// Clean up
		return () => {
			window.removeEventListener('keydown', handleKeyDown, true);
		};
	}, [onClose, identifier]);
	
	
	// Use ref
	const mouseDownTargetRef = useRef<EventTarget | null>(null);
	
	
	// Return JSX
	return ReactDOM.createPortal(
		<Container id={`${identifier}-modal`} aria-hidden='true'>
			<Fade
				tabIndex={-1}
				onMouseDown={(event) => {
					mouseDownTargetRef.current = event.target;
				}}
				onClick={(event) => {
					if (event.target === event.currentTarget && mouseDownTargetRef.current === event.currentTarget) {
						event.stopPropagation();
						handleClose();
					}
					
					mouseDownTargetRef.current = null;
				}}
			>
				<Dialog wide={wide} role='dialog' aria-modal='true' aria-labelledby={`${identifier}-modal-title`}>
					<CloseButton
						{...(communityThemeColor ? { $overrideColor: communityThemeColor } : { color: 'danger' })}
						type='button'
						aria-label='Close modal'
						onClick={handleClose}
					>
						<FontAwesomeIcon icon={faXmark} />
					</CloseButton>
					
					{typeof title === 'string' ? (
						<PaddedHeader>
							<Title id={`${identifier}-modal-title`}>{title}</Title>
						</PaddedHeader>
					) : (
						<PlainHeader id={`${identifier}-modal-title`}>{title}</PlainHeader>
					)}
					
					{children}
				</Dialog>
			</Fade>
		</Container>,
		document.body
	);
};

export default Modal;
